/**
 * FE#180 — plain-language facts about a bot trigger's scope.
 *
 * The trigger scope is a single `contacts` list whose meaning flips on
 * `triggerType` ('allowList' | 'blockList'):
 *   - block-list → respond to EVERYONE **except** the listed chats
 *   - allow-list → respond ONLY to the listed chats
 * and the empty cases invert: an empty block-list responds to everyone, an empty
 * allow-list responds to no one. That inversion is the FE#180 trap — a block-list
 * holding only a group means "respond everywhere except that group", so every
 * direct message still fires (Hernan's 145-DM credit burn).
 *
 * NOTE (#65): a block-list agent never fires in a GROUP — the backend
 * (`decideShouldTrigger`, BE#269) requires an explicit allow-list entry for a
 * group. So a group in a block-list is inert, and the copy must not promise that
 * "every other group still triggers it".
 *
 * These pure facts feed the editor's live-preview + the "responds to ALL direct
 * messages" guardrail. They do NOT change the runtime match semantics
 * (`isSenderInTriggerScope` in trigger-analysis.ts), which are correct.
 */
import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { TriggerType } from '@common/types/trigger.types';

/** A WhatsApp group JID ends in `@g.us`; DMs are `@c.us` / `@lid` / `@s.whatsapp.net`. */
export const isGroupJid = (jid: string): boolean =>
  chatKindOf(jid) === 'group';

export type TriggerScopeKind = 'everyone' | 'no_one' | 'only_listed' | 'everyone_except';

export interface TriggerScopeFacts {
  kind: TriggerScopeKind;
  contactCount: number;
  groupCount: number;
  individualCount: number;
  /** true when the config responds to ALL direct messages (the high-credit-burn case). */
  respondsToAllDirectMessages: boolean;
  /**
   * true when the scope looks INVERTED: a block-list holding ONLY groups. The user
   * almost always meant "respond only in these groups" (an allow-list), not
   * "respond everywhere except these groups" (which still answers every DM).
   */
  likelyInverted: boolean;
}

export function analyzeTriggerScopeFacts(data: {
  triggerType: TriggerType;
  contacts?: string[];
}): TriggerScopeFacts {
  const contacts = Array.isArray(data.contacts) ? data.contacts.filter((c) => typeof c === 'string') : [];
  const contactCount = contacts.length;
  const groupCount = contacts.filter(isGroupJid).length;
  const individualCount = contactCount - groupCount;
  const isBlockList = data.triggerType === 'blockList';

  if (isBlockList) {
    // A block-list ALWAYS answers direct messages that aren't individually listed —
    // and you can't enumerate "all DMs" — so it responds to (essentially) all DMs.
    const respondsToAllDirectMessages = true;
    if (contactCount === 0) {
      return { kind: 'everyone', contactCount, groupCount, individualCount, respondsToAllDirectMessages, likelyInverted: false };
    }
    return {
      kind: 'everyone_except',
      contactCount,
      groupCount,
      individualCount,
      respondsToAllDirectMessages,
      likelyInverted: false,
    };
  }

  // allow-list
  if (contactCount === 0) {
    return { kind: 'no_one', contactCount, groupCount, individualCount, respondsToAllDirectMessages: false, likelyInverted: false };
  }
  return {
    kind: 'only_listed',
    contactCount,
    groupCount,
    individualCount,
    respondsToAllDirectMessages: false,
    likelyInverted: false,
  };
}
